import { useContext, useState } from "react";
import BeelditTableHeaderActions from "@beeldit/core/components/BeelditTableHeaderActions";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { t } from "i18next";

import { Card } from "react-bootstrap";
import useListAction from "@beeldit/core/actions/list/useListAction";
import useCreateAction from "../actions/create/useCreateAction";
import useRemoveAction from "@beeldit/core/actions/remove/useRemoveAction";
import { getTableConfig } from "@beeldit/core/helpers/tables";
import usePayAction from "../actions/pay/usePayAction";
import useGenerateDeliveryNoteAction from "../actions/generate-delivery-note/useGenerateDeliveryNoteAction";
import useGenerateInvoiceAction from "../actions/generate-invoice/useGenerateInvoiceAction";
import { ListActionContext } from "@beeldit/core/actions/list/ListActionContext";
import useCancelAction from "../actions/cancel/useCancelAction";
import useUncancelAction from "../actions/uncancel/useUncancelAction";
import useEditAction from "../actions/edit/useEditAction";
import useUnpayAction from "../actions/unpay/useUnpayAction";
import useEmailAction from "../actions/email/useEmailAction";
import useExportAction from "../actions/export/useExportAction";
import useGenerateInvoicePdfAction from "../actions/generate-invoice-pdf/useGenerateInvoicePdfAction";
import useRegenerateInvoicePdfAction from "../actions/regenerate-invoice-pdf/useRegenerateInvoicePdfAction";
import useDownloadPDFAction from "@main/modules/bloonde/billing/invoices/actions/download-pdf/DownloadPDFAction";
import useEditInvoiceAction from "@main/modules/bloonde/billing/invoices/actions/edit/useEditAction";
import { EditActionProvider } from "@main/modules/bloonde/billing/invoices/actions/edit/EditActionContext";
import InvoiceAccountSelector from "@main/modules/bloonde/billing/invoice-accounts/components/InvoiceAccountSelector";
import useGenerateDeliveryNotePdfAction from "../actions/generate-delivery-note-pdf/useGenerateDeliveryNotePdfAction";
import useDownloadDeliveryNotePDFAction from "@main/modules/bloonde/billing/delivery-notes/actions/download-pdf/DownloadPDFAction";
import useEditDeliveryNoteAction from "@main/modules/bloonde/billing/delivery-notes/actions/edit/useEditAction";
import { EditActionProvider as DeliveryNoteEditActionProvider } from "@main/modules/bloonde/billing/delivery-notes/actions/edit/EditActionContext";
import { GenerateInvoicePdfActionProvider } from "../actions/generate-invoice-pdf/GenerateInvoicePdfActionContext";
import { RegenerateInvoicePdfActionProvider } from "../actions/regenerate-invoice-pdf/RegenerateInvoicePdfActionContext";
import { GenerateDeliveryNotePdfActionProvider } from "../actions/generate-delivery-note-pdf/GenerateDeliveryNotePdfActionContext";
import replicateInvoice from "@main/modules/bloonde/billing/invoices/actions/replicate/replicateInvoice";

function DepartureTableInner(props: { modelConfig: any; templates?: [] }) {
  const { modelConfig } = props;
  const { user } = useContext(AuthContext);
  const [showFilters, setShowFilters] = useState(false);

  // Semilla de filtros: hoy para evitar carga inicial sin filtros
  const today = new Date().toISOString().split("T")[0];
  const initialFilters = {
    checked_out_from: today,
    checked_out_to: today,
  };

  const {
    indexFunction,
    actionTemplate: listActionTemplate,
    setTableConfig,
  } = useListAction(modelConfig, user, initialFilters);

  const { initializeFunction, actionTemplate: createActionTemplate } =
    useCreateAction(modelConfig, indexFunction);

  const { prepareRemoveFunction, actionTemplate: removeActionTemplate } =
    useRemoveAction(modelConfig, indexFunction);

  const { preparePayFunction, actionTemplate: payActionTemplate } =
    usePayAction(modelConfig, indexFunction);

  const { prepareUnpayFunction, actionTemplate: unpayActionTemplate } =
    useUnpayAction(modelConfig, indexFunction);

  const { prepareCancelFunction, actionTemplate: cancelActionTemplate } =
    useCancelAction(modelConfig, indexFunction);

  const { prepareUncancelFunction, actionTemplate: uncancelActionTemplate } =
    useUncancelAction(modelConfig, indexFunction);

  const {
    prepareGenerateDeliveryNoteFunction,
    actionTemplate: _generateDeliveryNoteActionTemplate,
  } = useGenerateDeliveryNoteAction(modelConfig, indexFunction);

  const {
    prepareGenerateInvoiceFunction,
    actionTemplate: _generateInvoiceActionTemplate,
  } = useGenerateInvoiceAction(modelConfig, indexFunction);

  const { getFunction, actionTemplate: editActionTemplate } = useEditAction(
    modelConfig,
    indexFunction,
    prepareCancelFunction,
    prepareUncancelFunction
  );

  const { prepareEmailFunction, actionTemplate: emailActionTemplate } =
    useEmailAction(modelConfig, indexFunction);

  const { exportFunction, actionTemplate: exportActionTemplate } =
    useExportAction({ type: "departures" });
  const {
    prepareGenerateInvoicePdfFunction,
    actionTemplate: _generateInvoicePdfActionTemplate,
  } = useGenerateInvoicePdfAction(modelConfig, indexFunction);
  const {
    prepareRegenerateInvoicePdfFunction,
    actionTemplate: _regenerateInvoicePdfActionTemplate,
  } = useRegenerateInvoicePdfAction(modelConfig, indexFunction);
  const { downloadPDFFunction } = useDownloadPDFAction();
  const { getFunction: getInvoiceFunction, actionTemplate: editInvoiceActionTemplate } = useEditInvoiceAction(
    indexFunction,
    {},
    { receiver_accountable_id: InvoiceAccountSelector },
    {},
    true
  );

  const {
    prepareGenerateDeliveryNotePdfFunction,
    actionTemplate: generateDeliveryNotePdfActionTemplate,
  } = useGenerateDeliveryNotePdfAction(modelConfig, indexFunction);
  const { downloadPDFFunction: downloadDeliveryNotePDFFunction } = useDownloadDeliveryNotePDFAction();
  const { getFunction: getDeliveryNoteFunction, actionTemplate: editDeliveryNoteActionTemplate } = useEditDeliveryNoteAction(
    indexFunction,
    {},
    { receiver_accountable_id: InvoiceAccountSelector },
    {},
    true
  );

  const { tableData } = useContext(ListActionContext);

  /**<newActionsHooks**/

  /**
   * Table configuration definition
   */
  let tableConfig = getTableConfig(modelConfig.tableConfig, {
    indexFunction: indexFunction,
    actionsFunctions: [
      {
        name: "create",
        function: initializeFunction,
      },
      {
        name: "download",
        function: exportFunction,
      },
    ],
    bulkFunctions: [
      {
        name: "create-delivery-note",
        function: prepareGenerateDeliveryNoteFunction,
      },
      {
        name: "create-invoice",
        function: prepareGenerateInvoiceFunction,
      },
      /**<newBulkActions**/
    ],
    rowFunctions: [
      {
        name: "edit",
        function: getFunction,
      },
      {
        name: "delete",
        function: prepareRemoveFunction,
      },
      {
        name: "pay",
        function: preparePayFunction,
      },
      {
        name: "unpay",
        function: prepareUnpayFunction,
      },
      {
        name: "email",
        function: prepareEmailFunction,
      },
      {
        name: "cancel",
        function: prepareCancelFunction,
      },
      {
        name: "uncancel",
        function: prepareUncancelFunction,
      },
      {
        name: "generate-invoice-pdf",
        function: (invoiceId: any) => prepareGenerateInvoicePdfFunction(invoiceId),
      },
      {
        name: "regenerate-invoice-pdf",
        function: (invoiceId: any) => prepareRegenerateInvoicePdfFunction(invoiceId),
      },
      {
        name: "download-invoice-pdf",
        function: (url: any) => downloadPDFFunction(url),
      },
      {
        name: "edit-invoice",
        function: (invoiceId: any) => getInvoiceFunction(invoiceId),
      },
      {
        name: "replicate-invoice",
        function: (invoiceId: any) => {
          replicateInvoice(invoiceId, (newInvoiceId: number) => {
            getInvoiceFunction(newInvoiceId);
          });
        },
      },
      {
        name: "generate-delivery-note-pdf",
        function: (deliveryNoteId: any) => prepareGenerateDeliveryNotePdfFunction(deliveryNoteId),
      },
      {
        name: "download-delivery-note-pdf",
        function: (url: any) => downloadDeliveryNotePDFFunction(url),
      },
      {
        name: "edit-delivery-note",
        function: (deliveryNoteId: any) => getDeliveryNoteFunction(deliveryNoteId),
      },
      {
        name: "replicate-delivery-note",
        function: (deliveryNoteId: any) => {
          replicateInvoice(deliveryNoteId, (newDeliveryNoteId: number) => {
            getDeliveryNoteFunction(newDeliveryNoteId);
          });
        },
      },
    ],
  });
  setTableConfig(tableConfig);

  return (
    <Card>
      <Card.Header className="d-flex align-items-center justify-content-between ps-3 bg-white">
        <span>{t(modelConfig.modelPluralName)}</span>
        <div>
          <span className="card-header-subtitle">
            Total: {tableData ? tableData.totalElements : "-"} reservas
          </span>
          <span className="card-header-subtitle">
            Por salir: {tableData ? (tableData as any)["countCheckedOut"] : "-"}{" "}
            reservas
          </span>
        </div>
        <div>
          <BeelditTableHeaderActions
            actions={tableConfig.actions}
            bulkActions={tableConfig.bulkActions}
          ></BeelditTableHeaderActions>
        </div>
      </Card.Header>

      <Card.Body>
        <div className="d-flex justify-content-end filters-wrapper d-lg-none">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm filters-toggle"
            onClick={() => setShowFilters((v) => !v)}
          >
            {showFilters ? t("Ocultar filtros") : t("Mostrar filtros")}
          </button>
        </div>
        <div className={`filters-scope ${showFilters ? "filters-open" : ""}`}>
          <div className="table-responsive-wrap">{listActionTemplate}</div>
        </div>
        {createActionTemplate}
        {editActionTemplate}
        {removeActionTemplate}
        {payActionTemplate}
        {unpayActionTemplate}
        {cancelActionTemplate}
        {uncancelActionTemplate}
        {emailActionTemplate}
        {exportActionTemplate}
  {_generateDeliveryNoteActionTemplate}
  {_generateInvoiceActionTemplate}
  {_generateInvoicePdfActionTemplate}
  {_regenerateInvoicePdfActionTemplate}
  {editInvoiceActionTemplate}
  {generateDeliveryNotePdfActionTemplate}
  {editDeliveryNoteActionTemplate}
        {/* newTemplateActions */}
      </Card.Body>
    </Card>
  );
}

export default function DepartureTablePage(props: { modelConfig: any; templates?: [] }) {
  return (
    <GenerateInvoicePdfActionProvider>
      <RegenerateInvoicePdfActionProvider>
        <GenerateDeliveryNotePdfActionProvider>
          <EditActionProvider>
            <DeliveryNoteEditActionProvider>
              <DepartureTableInner {...props} />
            </DeliveryNoteEditActionProvider>
          </EditActionProvider>
        </GenerateDeliveryNotePdfActionProvider>
      </RegenerateInvoicePdfActionProvider>
    </GenerateInvoicePdfActionProvider>
  );
}
